import { getSteeringHubPageTitleFromPathname } from 'utils/steeringHubPageTitle';
import type { TenantConfig } from 'context/TenantConfigContext';
import { BREADCRUMB_SEGMENTS } from 'components/breadcrumb/breadcrumbConstants';
import { runtimeConfig } from 'config/runtimeConfig';

type MatomoEventArg = string | number;

export type MatomoEvent = [category: string, action: string, name?: string, value?: MatomoEventArg];

/**
 * Structured event-name input for component interactions that need stable prefixes plus contextual details.
 */
export interface MatomoEventNameBuilder {
  defaultNamePrefix: string;
  details?: (string | number | undefined | null)[];
  testid?: string;
  instanceId?: string;
}

/**
 * Public Matomo tracking API used across the app.
 *
 * Use `category` for business-level event families such as filters or trading actions. When omitted, the category is
 * derived from `pathname`, or falls back to `window.location.pathname`.
 *
 * `name` accepts either a final event name string or a structured builder for reusable UI interaction events.
 * Set `includePageContextInAction` only for legacy hook-style events that intentionally include the page category
 * inside the action label as well.
 */
export interface TrackMatomoEventParams {
  action: string;
  category?: string;
  name?: string | MatomoEventNameBuilder;
  pathname?: string;
  value?: MatomoEventArg;
  includePageContextInAction?: boolean;
}

function getMatomoCustomDimensionId(envValue: string | undefined, fallback: number): number {
  if (!envValue) return fallback;
  const parsed = Number.parseInt(envValue, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export const MATOMO_CUSTOM_DIMENSION_ACCOUNT_NUMBER = getMatomoCustomDimensionId(
  runtimeConfig.MATOMO_CUSTOM_DIMENSION_ACCOUNT_NUMBER_ID,
  3
);
export const MATOMO_CUSTOM_DIMENSION_MODULE = getMatomoCustomDimensionId(
  runtimeConfig.MATOMO_CUSTOM_DIMENSION_MODULE_ID,
  5
);
export const MATOMO_CUSTOM_DIMENSION_PERMISSION = getMatomoCustomDimensionId(
  runtimeConfig.MATOMO_CUSTOM_DIMENSION_PERMISSION_ID,
  6
);

function sendRawMatomoEvent(category: string, action: string, name?: string, value?: MatomoEventArg): void {
  if (typeof window === 'undefined') return;
  // eslint-disable-next-line no-underscore-dangle
  window._paq = window._paq ?? [];
  const event: MatomoEventArg[] = ['trackEvent', category, action];
  if (name !== undefined) event.push(name);
  if (value !== undefined) event.push(value);
  // eslint-disable-next-line no-underscore-dangle
  window._paq.push(event);
}

function getCurrentMatomoPathname(pathname?: string): string | undefined {
  if (pathname) return pathname;
  if (typeof window === 'undefined') return undefined;
  return window.location.pathname;
}

function buildMatomoEventName(name: string | MatomoEventNameBuilder): string {
  if (typeof name === 'string') return name;

  const contextPrefix = name.testid || `${name.defaultNamePrefix}-${name.instanceId ?? 'unknown'}`;
  const detailsPath = (name.details || []).filter(Boolean).join(' | ');

  return detailsPath ? `${contextPrefix} | ${detailsPath}` : contextPrefix;
}

/**
 * Tracks a Matomo event using the app's shared conventions for page-aware categories and UI event naming.
 */
export function trackMatomoEvent({
  action,
  category,
  name,
  pathname,
  value,
  includePageContextInAction = false,
}: TrackMatomoEventParams): void {
  const resolvedPathname = getCurrentMatomoPathname(pathname);
  const resolvedCategory = category ?? getSanitizedMatomoCategory(resolvedPathname ?? '');
  const resolvedAction = includePageContextInAction ? `${resolvedCategory} - ${action}` : action;
  const resolvedName = name ? buildMatomoEventName(name) : undefined;

  sendRawMatomoEvent(resolvedCategory, resolvedAction, resolvedName, value);
}

/**
 * Sets a Matomo Custom Dimension value.
 *
 * The `dimensionId` must match the ID configured in the Matomo dashboard under the scope you're targeting
 * (Visit Dimensions vs Action Dimensions — IDs don't overlap between the two).
 */
export function setMatomoCustomDimension(dimensionId: number, value: MatomoEventArg): void {
  if (typeof window === 'undefined') return;
  // eslint-disable-next-line no-underscore-dangle
  window._paq = window._paq ?? [];
  // eslint-disable-next-line no-underscore-dangle
  window._paq.push(['setCustomDimension', dimensionId, value]);
}

export function getMatomoPageTitle(pathname: string): string {
  return getSteeringHubPageTitleFromPathname(pathname) ?? document.title ?? pathname;
}

function pushMatomoDataLayerEvent(event: { event: string }): void {
  const dataLayer: MatomoDataLayerEvent[] = Reflect.get(window, '_mtm') ?? [];
  dataLayer.push(event);
  Reflect.set(window, '_mtm', dataLayer);
}

export function trackMatomoPageView(url: string, pageTitle: string): void {
  if (typeof window === 'undefined') return;

  if (window.tbLastTrackedMatomoPageUrl === url) {
    return;
  }

  // eslint-disable-next-line no-underscore-dangle
  window._paq = window._paq ?? [];
  // eslint-disable-next-line no-underscore-dangle
  window._paq.push(['setCustomUrl', url]);
  // eslint-disable-next-line no-underscore-dangle
  window._paq.push(['setDocumentTitle', pageTitle]);
  window.tbLastTrackedMatomoPageUrl = url;
  pushMatomoDataLayerEvent({ event: 'tb360.PageView' });
}

export function installMatomoHeatmapSessionRecordingTrigger(shouldRecord: boolean): void {
  if (typeof window === 'undefined') return;

  // eslint-disable-next-line no-underscore-dangle
  window._paq = window._paq ?? [];
  // eslint-disable-next-line no-underscore-dangle
  window._paq.push(['HeatmapSessionRecording::setTrigger', () => shouldRecord]);
}

export function getMatomoPermissionDimensionValueFromTenantPermissions(
  tenantPermissions: TenantConfig['permissions']
): string | undefined {
  const values = Array.from(new Set(tenantPermissions.filter(Boolean))).sort((a, b) => a.localeCompare(b));
  if (values.length === 0) return undefined;
  return values.join(',');
}

export function getMatomoModuleKeyFromPathname(pathname: string): string | undefined {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (normalized === '/' || normalized === '') return undefined;

  if (
    /^\/steeringhub(\/insightsfeed(\/|$)|\/?$)/.test(normalized) ||
    /^\/(insightsfeed|trading)(\/|$)/.test(normalized)
  ) {
    return 'trading';
  }
  if (/^\/(insights|insights-pro)(\/|$)/.test(normalized)) return 'insights';
  if (/^\/benchmarking(\/|$)/.test(normalized)) return 'benchmarking';
  if (/^\/articlevisibility(\/|$)/.test(normalized)) return 'articlevisibility';
  if (/^\/api(\/|$)/.test(normalized)) return 'api';

  const firstSegment = normalized.replace(/^\/+/, '').split('/')[0];
  return firstSegment || undefined;
}

const STATIC_PATH_WHITELIST = new Set([
  ...Object.values(BREADCRUMB_SEGMENTS),
  'insightsfeed',
  'article',
  'price-analysis',
  'drilldown',
  'marketshare',
  'detail',
  'style',
  'demand-monitoring',
]);

export const getSanitizedMatomoCategory = (pathname: string): string => {
  return (
    pathname
      .split('/')
      .filter(segment => {
        const cleanSegment = segment.toLowerCase();
        return STATIC_PATH_WHITELIST.has(cleanSegment) && !/^\d+$/.test(cleanSegment);
      })
      .join(' > ') || 'Unmapped'
  );
};
